import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import { apiGet, apiPost } from "../lib/api.js";
import { AdminTabs } from "./_AdminTabs.js";

/**
 * Admin "Find vendors" — search → classify → bulk-crawl.
 *
 *   1. Type a Serper-style query ("wedding florist brooklyn").
 *   2. See hits with auto-classified categories.
 *   3. Tick the keepers, click "Crawl selected" → enqueues full crawls via
 *      the existing POST /admin/crawl and shows trace links to the live
 *      /admin/crawl/:traceId logs.
 */

interface ClassifiedCategory {
  slug: string;
  confidence: number;
  is_primary: boolean;
}

interface AlreadyInIndex {
  vendor_id: string | null;            // null = attempted but didn't publish
  vendor_status: string | null;
  crawl_status: string;
  last_error: string | null;
}

interface FindResult {
  title: string;
  url: string;
  raw_link: string;
  snippet: string;
  homepage_status: "ok" | "fetch_failed" | "classify_failed";
  fetch_error: string | null;
  categories: ClassifiedCategory[];
  already_in_index: AlreadyInIndex | null;
}

interface AggregatorSummary {
  url: string;
  title: string;
  snippet: string;
  host: string;
}

interface SkippedResult {
  url: string;
  title: string;
  host: string;
  reason: string;
}

interface FindCost {
  trace_id: string;
  cost_micros: number;
  tokens_in: number;
  tokens_out: number;
  event_count: number;
}

interface FindResponse {
  query: string;
  results: FindResult[];
  existing: FindResult[];
  aggregators: AggregatorSummary[];
  skipped: SkippedResult[];
  pages_fetched: number;
  category: string | null;
  cost: FindCost;
}

interface CategoryOption {
  slug: string;
  label: string;
}

interface AutoCrawlRun {
  id: string;
  status: string;            // queued | running | done | failed
  location: string;
  per_category: number;
  categories_json: string;
  error: string | null;
  note: string | null;
  created_at_utc_ms: number;
  finished_at_utc_ms: number | null;
}

interface AutoCrawlItem {
  id: string;
  category: string;
  category_label: string;
  position: number;
  url: string;
  status: string;            // queued | crawling | saved | failed | skipped
  vendor_id: string | null;
  vendor_name: string | null;
  error: string | null;
}

interface AutoCrawlRunResponse {
  run: AutoCrawlRun;
  items: AutoCrawlItem[];
}

/** GET /admin/auto-crawl — ongoing runs for the banner. */
interface ActiveRun {
  id: string;
  status: string;            // queued | running
  location: string;
  per_category: number;
  categories_json: string;
  created_at_utc_ms: number;
  items_total: number;
  items_saved: number;
  items_failed: number;
  items_skipped: number;
  items_queued: number;
  crawling_url: string | null;
}

/** The two NYC presets offered for auto-crawl (default = surrounding area). */
const AUTO_CRAWL_LOCATIONS = [
  "New York City & surrounding area",
  "New York City",
] as const;

/** Format cost in cents with micros-level precision (mirrors AdminCrawl). */
const formatCost = (micros: number): string => {
  const cents = micros / 10_000;
  if (cents < 0.00001) return "0¢";
  return `${cents.toFixed(5)}¢`;
};

/** Dollars-equivalent for the same micros, shown in brackets next to cents. */
const formatCostDollars = (micros: number): string => {
  const dollars = micros / 1_000_000;
  return `$${dollars.toFixed(7)}`;
};

interface QueuedCrawl {
  url: string;
  trace_id: string | null;
  error: string | null;
}

export function AdminFind() {
  return (
    <>
      <SignedOut><RedirectToSignIn /></SignedOut>
      <SignedIn><AdminFindInner /></SignedIn>
    </>
  );
}

function AdminFindInner() {
  const { getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") ?? "";
  const initialCategory = searchParams.get("cat") ?? "";
  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState(initialCategory);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<FindResponse | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [queueing, setQueueing] = useState(false);
  const [queued, setQueued] = useState<QueuedCrawl[] | null>(null);

  // --- auto-crawl section state (kept in component state, NOT searchParams, so
  // its polling never re-triggers the manual ?q=/?cat= search effect) ---
  const [acCategories, setAcCategories] = useState<Set<string>>(new Set());
  const [acPerCategory, setAcPerCategory] = useState(2);
  const [acLocation, setAcLocation] = useState<string>(AUTO_CRAWL_LOCATIONS[0]);
  const [acSubmitting, setAcSubmitting] = useState(false);
  const [acError, setAcError] = useState<string | null>(null);
  const [acRunId, setAcRunId] = useState<string | null>(null);
  const [acRun, setAcRun] = useState<AutoCrawlRunResponse | null>(null);

  // --- ongoing-runs banner state: poll GET /admin/auto-crawl so an operator
  // landing on the page (or after a hard refresh that lost acRunId) still sees
  // any in-flight run, can re-attach to its progress, or cancel it. ---
  const [activeRuns, setActiveRuns] = useState<ActiveRun[]>([]);
  const [cancellingRunId, setCancellingRunId] = useState<string | null>(null);
  const [activeRunsTick, setActiveRunsTick] = useState(0);

  // Load the category filter options once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await apiGet<{ categories: CategoryOption[] }>("/admin/categories", token);
      if (cancelled || !res.ok) return;
      setCategories(res.value.categories);
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  // Run search whenever the ?q= or ?cat= changes (also covers initial load).
  useEffect(() => {
    const q = searchParams.get("q");
    const cat = searchParams.get("cat") ?? "";
    if (!q || q.trim() === "") return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setError(null);
      setData(null);
      setSelected(new Set());
      setQueued(null);
      const token = await getToken();
      if (!token || cancelled) { setBusy(false); return; }
      const res = await apiPost<FindResponse>("/admin/search", token, {
        query: q,
        ...(cat ? { category: cat } : {}),
      });
      if (cancelled) return;
      setBusy(false);
      if (!res.ok) {
        setError(JSON.stringify(res.error));
        return;
      }
      setData(res.value);
    })();
    return () => { cancelled = true; };
  }, [searchParams, getToken]);

  const submit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q || busy) return;
    setSearchParams(category ? { q, cat: category } : { q });
  }, [query, category, busy, setSearchParams]);

  // Poll the active auto-crawl run until it reaches a terminal state. Mirrors
  // AdminCrawl's setTimeout(poll)+cancelled+clearTimeout shape (not setInterval)
  // so an in-flight request can't stack a backlog of polls.
  useEffect(() => {
    if (!acRunId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await apiGet<AutoCrawlRunResponse>(`/admin/auto-crawl/${acRunId}`, token);
      if (cancelled) return;
      if (res.ok) {
        setAcRun(res.value);
        const s = res.value.run.status;
        if (s === "done" || s === "failed" || s === "cancelled") return;
      }
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [acRunId, getToken]);

  // Poll the ongoing-runs list (10s — a cheap D1 aggregate). Same
  // setTimeout+cancelled shape as the run poll so requests never stack.
  // `activeRunsTick` forces an immediate refresh after start/cancel actions.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await apiGet<{ runs: ActiveRun[] }>("/admin/auto-crawl", token);
      if (cancelled) return;
      if (res.ok) setActiveRuns(res.value.runs);
      timer = setTimeout(poll, 10_000);
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [getToken, activeRunsTick]);

  const cancelRun = useCallback(async (runId: string) => {
    if (cancellingRunId) return;
    if (!window.confirm("Cancel this auto-crawl run? The current crawl is stopped and remaining candidates won't be crawled. Vendors already saved stay published.")) return;
    setCancellingRunId(runId);
    setAcError(null);
    const token = await getToken();
    if (!token) { setCancellingRunId(null); return; }
    const res = await apiPost<{ ok: true }>(`/admin/auto-crawl/${runId}/cancel`, token, {});
    setCancellingRunId(null);
    if (!res.ok) { setAcError(JSON.stringify(res.error)); return; }
    setActiveRunsTick((t) => t + 1);
  }, [cancellingRunId, getToken]);

  const toggleAcCategory = (slug: string) => {
    setAcCategories((cur) => {
      const next = new Set(cur);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  // Select-all toggle for the auto-crawl category dropdown: when every loaded
  // category is already selected, clear; otherwise select them all. The server
  // caps the set at 50 (admin.ts AutoCrawlBody), comfortably above the taxonomy.
  const allAcSelected = categories.length > 0 && categories.every((c) => acCategories.has(c.slug));
  const toggleAllAcCategories = () => {
    setAcCategories(allAcSelected ? new Set() : new Set(categories.map((c) => c.slug)));
  };

  const submitAutoCrawl = useCallback(async () => {
    if (acSubmitting || acCategories.size === 0) return;
    setAcSubmitting(true);
    setAcError(null);
    setAcRun(null);
    const token = await getToken();
    if (!token) { setAcSubmitting(false); return; }
    // Submit in the dropdown's display order (taxonomy sort_order).
    const ordered = categories.map((c) => c.slug).filter((s) => acCategories.has(s));
    const res = await apiPost<{ run_id: string }>("/admin/auto-crawl", token, {
      categories: ordered,
      per_category: acPerCategory,
      location: acLocation,
    });
    setAcSubmitting(false);
    if (!res.ok) { setAcError(JSON.stringify(res.error)); return; }
    setAcRunId(res.value.run_id);
    setActiveRunsTick((t) => t + 1); // surface the new run in the banner immediately
  }, [acSubmitting, acCategories, acPerCategory, acLocation, categories, getToken]);

  const toggle = (url: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  // A row is INELIGIBLE only when we've already published a vendor at this
  // URL. Previously-attempted-but-failed URLs stay eligible so the operator
  // can retry-by-crawling — e.g. after fixing a robots.txt issue or rotating
  // out a broken extractor.
  const isEligible = (r: FindResult): boolean =>
    !(r.already_in_index && r.already_in_index.vendor_id);

  const toggleAll = () => {
    if (!data) return;
    const eligible = data.results.filter(isEligible).map((r) => r.url);
    const allSelected = eligible.length > 0 && eligible.every((u) => selected.has(u));
    setSelected(allSelected ? new Set() : new Set(eligible));
  };

  const crawlSelected = useCallback(async () => {
    if (selected.size === 0 || queueing) return;
    setQueueing(true);
    const token = await getToken();
    if (!token) { setQueueing(false); return; }
    const urls = Array.from(selected);
    const settled = await Promise.all(urls.map(async (url) => {
      const res = await apiPost<{ trace_id: string }>("/admin/crawl", token, { url });
      return res.ok
        ? { url, trace_id: res.value.trace_id, error: null }
        : { url, trace_id: null, error: JSON.stringify(res.error) };
    }));
    setQueueing(false);
    setQueued(settled);
    setSelected(new Set());
  }, [selected, queueing, getToken]);

  const eligibleCount = data?.results.filter(isEligible).length ?? 0;
  const allEligibleSelected = eligibleCount > 0 && data!.results
    .filter(isEligible)
    .every((r) => selected.has(r.url));

  return (
    <section className="max-w-5xl mx-auto px-6 py-8 pb-32">
      <AdminTabs />

      {activeRuns.length > 0 && (
        <OngoingRunsBanner
          runs={activeRuns}
          viewingRunId={acRunId}
          onView={(id) => setAcRunId(id)}
          onCancel={cancelRun}
          cancellingRunId={cancellingRunId}
        />
      )}

      <AutoCrawlPanel
        categories={categories}
        selected={acCategories}
        toggleCategory={toggleAcCategory}
        allSelected={allAcSelected}
        toggleAll={toggleAllAcCategories}
        perCategory={acPerCategory}
        setPerCategory={setAcPerCategory}
        location={acLocation}
        setLocation={setAcLocation}
        submitting={acSubmitting}
        error={acError}
        onSubmit={submitAutoCrawl}
      />

      {acRun && <AutoCrawlStatus data={acRun} />}

      <header className="mb-6">
        <h2 className="font-display text-3xl">Find vendors</h2>
        <p className="mt-1 text-sm text-stone-600">
          Run a Serper query, auto-classify each hit by loading its homepage,
          then tick the keepers and queue full crawls in bulk.
        </p>
      </header>

      <form onSubmit={submit} className="mb-8 flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={busy}
          placeholder="wedding florist brooklyn"
          className="flex-1 px-4 py-2 rounded-full border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900 text-sm"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          disabled={busy}
          aria-label="Filter by category"
          className="px-4 py-2 rounded-full border border-stone-300 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 text-sm"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
        <button
          disabled={busy || !query.trim()}
          className="px-5 py-2 rounded-full bg-stone-900 text-stone-50 hover:bg-stone-700 disabled:opacity-40"
        >
          {busy ? "Searching…" : "Search"}
        </button>
      </form>

      {error && (
        <pre className="mb-6 p-3 bg-red-50 text-red-900 rounded-lg font-mono text-xs whitespace-pre-wrap">
          {error}
        </pre>
      )}

      {busy && !data && (
        <div className="text-sm text-stone-500 italic">Searching + classifying…</div>
      )}

      {data && data.results.length === 0 && data.existing.length === 0
        && data.aggregators.length === 0 && data.skipped.length === 0 && (
        <div className="rounded-lg border border-stone-200 p-8 text-center text-stone-500">
          No results.
        </div>
      )}

      {data && (data.results.length > 0 || data.existing.length > 0
        || data.aggregators.length > 0 || data.skipped.length > 0) && (
        <ResultSummary
          results={data.results}
          existing={data.existing}
          aggregators={data.aggregators}
          skipped={data.skipped}
          pagesFetched={data.pages_fetched}
          cost={data.cost}
        />
      )}

      {data && data.existing.length > 0 && (
        <ExistingPanel existing={data.existing} />
      )}

      {data && data.skipped.length > 0 && (
        <SkippedPanel skipped={data.skipped} />
      )}

      {data && data.aggregators.length > 0 && (
        <AggregatorsPanel aggregators={data.aggregators} />
      )}

      {data && data.results.length > 0 && (
        <ResultsTable
          results={data.results}
          selected={selected}
          toggle={toggle}
          toggleAll={toggleAll}
          allEligibleSelected={allEligibleSelected}
          isEligible={isEligible}
        />
      )}

      {queued && <QueuedPanel rows={queued} />}

      {data && eligibleCount > 0 && (
        <FooterBar
          count={selected.size}
          eligible={eligibleCount}
          busy={queueing}
          onCrawl={crawlSelected}
        />
      )}
    </section>
  );
}

// --- subpanels --------------------------------------------------------------

function ResultsTable({
  results, selected, toggle, toggleAll, allEligibleSelected, isEligible,
}: {
  results: FindResult[];
  selected: Set<string>;
  toggle: (url: string) => void;
  toggleAll: () => void;
  allEligibleSelected: boolean;
  isEligible: (r: FindResult) => boolean;
}) {
  return (
    <div className="rounded-lg border border-stone-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-stone-600 text-xs">
          <tr>
            <th className="w-10 px-3 py-2 text-left">
              <input
                type="checkbox"
                checked={allEligibleSelected}
                onChange={toggleAll}
                className="rounded border-stone-300"
                aria-label="Select all unindexed"
              />
            </th>
            <th className="text-left px-3 py-2 font-medium">vendor</th>
            <th className="text-left px-3 py-2 font-medium">categories</th>
            <th className="text-left px-3 py-2 font-medium w-40">status</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => {
            const checked = selected.has(r.url);
            return (
              <tr key={r.url} className="border-t border-stone-100 align-top hover:bg-stone-50">
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(r.url)}
                    disabled={!isEligible(r)}
                    className="rounded border-stone-300"
                  />
                </td>
                <td className="px-3 py-3">
                  <a
                    href={r.raw_link}
                    target="_blank"
                    rel="noreferrer"
                    className="font-display text-base text-stone-900 hover:underline"
                  >
                    {r.title}
                  </a>
                  <div className="mt-0.5 text-xs text-stone-500 font-mono">{r.url.replace(/^https?:\/\//, "")}</div>
                  {r.snippet && (
                    <div className="mt-1 text-xs text-stone-600 line-clamp-2 max-w-2xl">{r.snippet}</div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <CategoryChips cats={r.categories} />
                  {r.categories.length === 0 && r.homepage_status === "ok" && (
                    <span className="text-xs text-stone-400 italic">uncategorized</span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs">
                  <StatusPill result={r} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Three-state status pill:
 *   - vendor — green; clicks through to /vendor/:id
 *   - crawl {status} — amber; tooltip carries the last_error for failed/skipped
 *   - fetch_failed / classify_failed — amber, distinct from crawl_source state
 *   - dash — never seen this URL before
 */
function StatusPill({ result }: { result: FindResult }) {
  const idx = result.already_in_index;
  if (idx?.vendor_id) {
    return (
      <Link
        to={`/vendor/${idx.vendor_id}`}
        className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
      >
        vendor
      </Link>
    );
  }
  if (idx) {
    return (
      <span
        className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
        title={idx.last_error ?? `crawl_source.status = ${idx.crawl_status}`}
      >
        crawl {idx.crawl_status}
      </span>
    );
  }
  if (result.homepage_status === "fetch_failed") {
    return (
      <span className="inline-flex flex-col gap-0.5">
        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 self-start" title={result.fetch_error ?? undefined}>
          fetch failed
        </span>
        {result.fetch_error && (
          <span className="text-[11px] text-stone-500 font-mono max-w-[10rem] truncate" title={result.fetch_error}>
            {result.fetch_error}
          </span>
        )}
      </span>
    );
  }
  if (result.homepage_status === "classify_failed") {
    return (
      <span className="inline-flex flex-col gap-0.5">
        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 self-start" title={result.fetch_error ?? undefined}>
          classify failed
        </span>
        {result.fetch_error && (
          <span className="text-[11px] text-stone-500 font-mono max-w-[10rem] truncate" title={result.fetch_error}>
            {result.fetch_error}
          </span>
        )}
      </span>
    );
  }
  return <span className="text-stone-400">—</span>;
}

function ResultSummary({
  results, existing, aggregators, skipped, pagesFetched, cost,
}: {
  results: FindResult[];
  existing: FindResult[];
  aggregators: AggregatorSummary[];
  skipped: SkippedResult[];
  pagesFetched: number;
  cost: FindCost;
}) {
  return (
    <div className="mb-3 text-xs text-stone-500 space-y-0.5">
      <div>
        {results.length} vendor candidate{results.length === 1 ? "" : "s"}
        {existing.length > 0 && <> · {existing.length} already indexed</>}
        {skipped.length > 0 && <> · {skipped.length} uncategorized skipped</>}
        {aggregators.length > 0 && <> · {aggregators.length} aggregator article{aggregators.length === 1 ? "" : "s"} skipped</>}
        {pagesFetched > 1 && <> · {pagesFetched} search pages scanned</>}
      </div>
      <div>
        cost: <strong>{formatCost(cost.cost_micros)}</strong> ({formatCostDollars(cost.cost_micros)})
        {" · "}
        {cost.tokens_in.toLocaleString()} in / {cost.tokens_out.toLocaleString()} out tokens
        {" · "}
        {cost.event_count} LLM call{cost.event_count === 1 ? "" : "s"}
      </div>
    </div>
  );
}

/**
 * Collapsed by default. Listicles, reddit threads, marketplace pages — the
 * stuff the operator doesn't want in their review queue but might want to
 * audit. We intentionally don't extract vendor links from these (token-heavy
 * for marginal yield); they're surfaced as-is for visibility only.
 */
function AggregatorsPanel({ aggregators }: { aggregators: AggregatorSummary[] }) {
  if (aggregators.length === 0) return null;
  return (
    <details className="mb-4 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-xs">
      <summary className="cursor-pointer text-stone-700">
        Skipped {aggregators.length} aggregator article{aggregators.length === 1 ? "" : "s"} (listicles / marketplaces / forums)
      </summary>
      <ul className="mt-2 space-y-1 font-mono">
        {aggregators.map((a) => (
          <li key={a.url} className="flex items-center justify-between gap-3">
            <a
              href={a.url}
              target="_blank"
              rel="noreferrer"
              className="text-stone-700 hover:underline truncate max-w-xl"
              title={a.title}
            >
              [{a.host}] {a.title}
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Collapsed by default. Hits that already map to a PUBLISHED vendor — kept out
 * of the crawl table (re-crawling them is wasteful) but surfaced here so the
 * operator can see "we already have this one" and click through to the profile.
 */
function ExistingPanel({ existing }: { existing: FindResult[] }) {
  if (existing.length === 0) return null;
  return (
    <details className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs">
      <summary className="cursor-pointer text-emerald-900">
        {existing.length} existing vendor{existing.length === 1 ? "" : "s"} (already in the index)
      </summary>
      <ul className="mt-2 space-y-1 font-mono">
        {existing.map((e) => {
          const host = e.url.replace(/^https?:\/\//, "");
          const vendorId = e.already_in_index?.vendor_id;
          return (
            <li key={e.url} className="flex items-center justify-between gap-3">
              <a
                href={e.raw_link}
                target="_blank"
                rel="noreferrer"
                className="text-stone-700 hover:underline truncate max-w-xl"
                title={e.title}
              >
                [{host}] {e.title}
              </a>
              {vendorId && (
                <Link
                  to={`/vendor/${vendorId}`}
                  className="shrink-0 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                >
                  vendor →
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/**
 * Collapsed by default. Hits we fetched + classified fine but that matched NO
 * wedding category — not crawl candidates, but worth showing so the operator
 * can spot a misclassified vendor or a query that's pulling off-topic sites.
 */
function SkippedPanel({ skipped }: { skipped: SkippedResult[] }) {
  if (skipped.length === 0) return null;
  return (
    <details className="mb-4 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-xs">
      <summary className="cursor-pointer text-stone-700">
        Skipped {skipped.length} uncategorized hit{skipped.length === 1 ? "" : "s"} (no matching wedding category)
      </summary>
      <ul className="mt-2 space-y-1 font-mono">
        {skipped.map((s) => (
          <li key={s.url} className="flex items-center justify-between gap-3">
            <a
              href={s.url}
              target="_blank"
              rel="noreferrer"
              className="text-stone-700 hover:underline truncate max-w-xl"
              title={s.title}
            >
              [{s.host}] {s.title}
            </a>
            <span className="text-stone-400 shrink-0">{s.reason}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Top-of-page bulk crawler. Pick categories + a per-category count + a NYC
 * location preset, then submit: the backend AutoCrawlWorkflow LLM-generates a
 * SERP query per category, finds candidate vendor sites, and crawls them one at
 * a time until `per_category` vendors save — then the next category. Live
 * progress renders in AutoCrawlStatus below.
 */
function AutoCrawlPanel({
  categories, selected, toggleCategory, allSelected, toggleAll, perCategory, setPerCategory,
  location, setLocation, submitting, error, onSubmit,
}: {
  categories: CategoryOption[];
  selected: Set<string>;
  toggleCategory: (slug: string) => void;
  allSelected: boolean;
  toggleAll: () => void;
  perCategory: number;
  setPerCategory: (n: number) => void;
  location: string;
  setLocation: (s: string) => void;
  submitting: boolean;
  error: string | null;
  onSubmit: () => void;
}) {
  const selectedLabels = categories.filter((c) => selected.has(c.slug)).map((c) => c.label);
  return (
    <div className="mb-8 rounded-xl border border-stone-300 bg-stone-50 p-5">
      <h3 className="font-display text-xl">Auto-crawl vendors</h3>
      <p className="mt-0.5 mb-4 text-sm text-stone-600">
        Pick categories and how many of each to find. We generate a search per
        category, then crawl candidates one at a time until the target is met.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        {/* multi-select category dropdown */}
        <details className="relative">
          <summary className="cursor-pointer list-none px-4 py-2 rounded-full border border-stone-300 bg-white text-sm min-w-[12rem]">
            {selectedLabels.length === 0
              ? "Select categories…"
              : selectedLabels.length <= 2
                ? selectedLabels.join(", ")
                : `${selectedLabels.length} categories`}
          </summary>
          <div className="absolute z-10 mt-1 w-64 max-h-72 overflow-auto rounded-lg border border-stone-200 bg-white p-2 shadow-lg">
            {categories.length === 0 && <div className="p-2 text-xs text-stone-400">loading…</div>}
            {categories.length > 0 && (
              <button
                type="button"
                onClick={toggleAll}
                className="w-full text-left px-2 py-1.5 mb-1 rounded text-xs font-medium text-stone-700 hover:bg-stone-100 border-b border-stone-100"
              >
                {allSelected ? "Clear all" : "Select all"}
              </button>
            )}
            {categories.map((c) => (
              <label key={c.slug} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-stone-50 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={selected.has(c.slug)}
                  onChange={() => toggleCategory(c.slug)}
                  className="rounded border-stone-300"
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
        </details>

        <label className="flex flex-col text-xs text-stone-600">
          per category
          <input
            type="number"
            min={1}
            max={10}
            value={perCategory}
            onChange={(e) => setPerCategory(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
            className="mt-0.5 w-20 px-3 py-2 rounded-full border border-stone-300 text-sm"
          />
        </label>

        <label className="flex flex-col text-xs text-stone-600">
          location
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="mt-0.5 px-4 py-2 rounded-full border border-stone-300 bg-white text-sm"
          >
            {AUTO_CRAWL_LOCATIONS.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </label>

        <button
          onClick={onSubmit}
          disabled={submitting || selected.size === 0}
          className="px-5 py-2 rounded-full bg-stone-900 text-stone-50 hover:bg-stone-700 disabled:opacity-40 text-sm"
        >
          {submitting ? "Starting…" : "Start auto-crawl"}
        </button>
      </div>
      {error && (
        <pre className="mt-3 p-3 bg-red-50 text-red-900 rounded-lg font-mono text-xs whitespace-pre-wrap">{error}</pre>
      )}
    </div>
  );
}

/**
 * Ongoing-runs banner: any run still open on the server (survives hard
 * refreshes that lose the in-memory acRunId). "View progress" re-attaches the
 * existing AutoCrawlStatus poll; "Cancel" terminates the run + its in-flight
 * crawl server-side.
 */
function OngoingRunsBanner({
  runs, viewingRunId, onView, onCancel, cancellingRunId,
}: {
  runs: ActiveRun[];
  viewingRunId: string | null;
  onView: (id: string) => void;
  onCancel: (id: string) => void;
  cancellingRunId: string | null;
}) {
  const categoryLabels = (json: string): string => {
    try {
      const cats = JSON.parse(json) as { label: string }[];
      const labels = cats.map((c) => c.label);
      return labels.length <= 3 ? labels.join(", ") : `${labels.slice(0, 3).join(", ")} +${labels.length - 3}`;
    } catch {
      return "?";
    }
  };
  const startedAgo = (ms: number): string => {
    const min = Math.max(0, Math.round((Date.now() - ms) / 60_000));
    if (min < 60) return `${min} min ago`;
    return `${Math.floor(min / 60)}h ${min % 60}m ago`;
  };

  return (
    <div className="mb-8 rounded-xl border border-amber-300 bg-amber-50 p-5">
      <h3 className="font-display text-xl">
        <span className="inline-block animate-pulse text-amber-700">● </span>
        Ongoing auto-crawl{runs.length > 1 ? "s" : ""}
      </h3>
      <ul className="mt-3 space-y-3">
        {runs.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <div className="min-w-0">
              <div className="font-medium text-stone-800">
                {categoryLabels(r.categories_json)}
                <span className="ml-2 font-normal text-stone-500 text-xs">
                  started {startedAgo(r.created_at_utc_ms)} · {r.per_category}/category · {r.location}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-stone-600">
                {r.items_saved} saved · {r.items_failed} failed · {r.items_skipped} skipped · {r.items_queued} queued
                {r.crawling_url && (
                  <span className="ml-2 text-amber-800">
                    crawling <span className="font-mono">{r.crawling_url.replace(/^https?:\/\//, "")}</span>…
                  </span>
                )}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              <button
                onClick={() => onView(r.id)}
                disabled={viewingRunId === r.id}
                className="px-4 py-1.5 rounded-full border border-stone-300 bg-white text-xs hover:bg-stone-100 disabled:opacity-40"
              >
                {viewingRunId === r.id ? "Viewing below" : "View progress"}
              </button>
              <button
                onClick={() => onCancel(r.id)}
                disabled={cancellingRunId !== null}
                className="px-4 py-1.5 rounded-full bg-red-700 text-white text-xs hover:bg-red-800 disabled:opacity-40"
              >
                {cancellingRunId === r.id ? "Cancelling…" : "Cancel"}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Live progress for an auto-crawl run: items grouped by category. */
function AutoCrawlStatus({ data }: { data: AutoCrawlRunResponse }) {
  const { run, items } = data;
  const savedCount = items.filter((i) => i.status === "saved").length;
  const running = run.status === "queued" || run.status === "running";
  // Preserve submitted/category order by first-seen.
  const order: string[] = [];
  for (const it of items) if (!order.includes(it.category)) order.push(it.category);
  const labelFor = (slug: string) => items.find((i) => i.category === slug)?.category_label ?? slug;

  return (
    <div className="mb-8 rounded-xl border border-stone-300 bg-white p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl">
          Auto-crawl{" "}
          <span className={
            run.status === "done" ? "text-emerald-700"
              : run.status === "failed" ? "text-red-700"
                : run.status === "cancelled" ? "text-stone-500"
                  : "text-amber-700"
          }>
            {running && <span className="inline-block animate-pulse">● </span>}{run.status}
          </span>
        </h3>
        <div className="text-xs text-stone-500">
          {savedCount} saved · {run.per_category}/category · {run.location}
        </div>
      </div>
      {run.error && <div className="mt-1 text-xs text-red-700 font-mono">{run.error}</div>}
      {run.note && <div className="mt-1 text-xs text-stone-500 whitespace-pre-line">{run.note}</div>}

      <div className="mt-4 space-y-4">
        {order.map((slug) => {
          const catItems = items.filter((i) => i.category === slug);
          const catSaved = catItems.filter((i) => i.status === "saved").length;
          return (
            <div key={slug}>
              <div className="text-sm font-medium text-stone-700">
                {labelFor(slug)} <span className="text-stone-400 font-normal">· {catSaved}/{run.per_category} saved</span>
              </div>
              <ul className="mt-1 space-y-1">
                {catItems.map((it) => (
                  <li key={it.id} className="flex items-center gap-3 text-xs">
                    <AutoCrawlStatusPill item={it} />
                    <span className="font-mono text-stone-600 truncate max-w-md">
                      {it.url.replace(/^https?:\/\//, "")}
                    </span>
                    {it.status === "saved" && it.vendor_id && (
                      <Link
                        to={`/vendor/${it.vendor_id}`}
                        className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 shrink-0"
                      >
                        {it.vendor_name ? `${it.vendor_name} →` : "view →"}
                      </Link>
                    )}
                    {(it.status === "failed" || it.status === "skipped") && it.error && (
                      <span className="text-stone-400 truncate max-w-xs" title={it.error}>{it.error}</span>
                    )}
                  </li>
                ))}
                {catItems.length === 0 && (
                  <li className="text-xs text-stone-400 italic">finding candidates…</li>
                )}
              </ul>
            </div>
          );
        })}
        {order.length === 0 && (
          <div className="text-xs text-stone-400 italic">starting…</div>
        )}
      </div>
    </div>
  );
}

function AutoCrawlStatusPill({ item }: { item: AutoCrawlItem }) {
  const map: Record<string, string> = {
    queued: "bg-stone-100 text-stone-600",
    crawling: "bg-amber-100 text-amber-800",
    saved: "bg-emerald-100 text-emerald-800",
    failed: "bg-red-100 text-red-800",
    skipped: "bg-stone-100 text-stone-500",
  };
  const label = item.status === "crawling" ? "crawling…" : item.status;
  return (
    <span className={`px-2 py-0.5 rounded-full shrink-0 ${map[item.status] ?? "bg-stone-100 text-stone-600"}`}>
      {label}
    </span>
  );
}

function CategoryChips({ cats }: { cats: ClassifiedCategory[] }) {
  if (cats.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {cats.map((c) => (
        <span
          key={c.slug}
          className={
            c.is_primary
              ? "px-2 py-0.5 rounded-full text-xs flex items-center gap-1 bg-stone-900 text-stone-50"
              : "px-2 py-0.5 rounded-full text-xs flex items-center gap-1 bg-stone-100 text-stone-700"
          }
        >
          {c.is_primary && <span>★</span>}
          <span>{c.slug}</span>
          <span className="opacity-60 font-mono">{c.confidence.toFixed(2)}</span>
        </span>
      ))}
    </div>
  );
}

function QueuedPanel({ rows }: { rows: QueuedCrawl[] }) {
  const okCount = rows.filter((r) => r.trace_id).length;
  return (
    <div className="mt-6 p-4 rounded-lg border border-emerald-200 bg-emerald-50">
      <div className="text-sm text-emerald-900">
        Queued <strong>{okCount}</strong> of {rows.length} crawls.
      </div>
      <ul className="mt-2 text-xs space-y-1 font-mono">
        {rows.map((r) => (
          <li key={r.url} className="flex items-center gap-3">
            <span className="text-stone-600 truncate max-w-md">{r.url.replace(/^https?:\/\//, "")}</span>
            {r.trace_id ? (
              <Link
                to={`/admin/crawl/${r.trace_id}`}
                className="px-2 py-0.5 rounded bg-stone-900 text-stone-50 hover:bg-stone-700"
              >
                trace →
              </Link>
            ) : (
              <span className="text-red-700">{r.error}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterBar({ count, eligible, busy, onCrawl }: { count: number; eligible: number; busy: boolean; onCrawl: () => void }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 border-t border-stone-200 bg-white/95 backdrop-blur px-6 py-3">
      <div className="max-w-5xl mx-auto flex items-center justify-between text-sm">
        <span className="text-stone-600">
          {count} of {eligible} selected
        </span>
        <button
          onClick={onCrawl}
          disabled={count === 0 || busy}
          className="px-5 py-2 rounded-full bg-stone-900 text-stone-50 hover:bg-stone-700 disabled:opacity-40"
        >
          {busy ? "Queueing…" : count === 0 ? "Crawl selected" : `Crawl ${count} selected`}
        </button>
      </div>
    </div>
  );
}
